<template>
	<row>

		<column :cols="12" v-if="name">
			<form-input v-model="data[`${propPrefix}name`]"
						:rules="{required: required, min: 2, max: 100}"
						:disabled="disabled"
						:name="`${validationPrefix}name`"
						:label="$t('label.name')"
						:help="!disableHelp ? $t(helpPrefix + '.name', null) : null"/>
		</column>

		<template v-if="!noAddress">
			<column cols="12" :lg="slim && showStates ? (showDistricts ? 4 : 6) : 12">
				<form-country-select v-model="data[`${propPrefix}country_code`]"
									 :rules="{required: required || countryRequired || data[`${propPrefix}city`] || data[`${propPrefix}street`] }"
									 :clearable="!required && !countryRequired"
									 :disabled="disabled"
									 :color="color"
									 :name="`${validationPrefix}country_code`"
									 :placeholder="withPlaceholders ? $t('placeholder.country_code') : null"
									 :help="!disableHelp ? $t(helpPrefix + '.country_code', null) : null"
									 @update:selected="handleChangedCountry"
									 @update:modelValue="handleLocationInput"
									 :label="$t('label.country')"/>
			</column>

			<column v-if="showStates" cols="12" :lg="slim && showStates ? (showDistricts ? 4 : 6) : 12">
				<form-state-select v-model="data[`${propPrefix}state_code`]"
								   :country="data[`${propPrefix}country_code`]"
								   :color="color"
								   :placeholder="withPlaceholders ? $t('placeholder.state_code') : null"
								   :rules="{required: required || countryRequired || data[`${propPrefix}city`] || data[`${propPrefix}street`]}"
								   :clearable="!required"
								   :disabled="disabled"
								   :name="`${validationPrefix}state_code`"
								   :help="!disableHelp ? $t(helpPrefix + '.state_code', null) : null"
								   @update:selected="handleChangedCountryState"
								   @update:modelValue="handleLocationInput"
								   :label="$t('label.state')"/>
			</column>

			<column v-if="showDistricts" cols="12" :lg="slim ? 4 : 12">
				<form-district-select v-model="data[`${propPrefix}district_id`]"
								   :country="data[`${propPrefix}country_code`]"
								   :state="data[`${propPrefix}state_code`]"
								   :color="color"
								   :placeholder="withPlaceholders ? $t('placeholder.select') : null"
								  :rules="{required: required || countryRequired || data[`${propPrefix}city`] || data[`${propPrefix}street`]}"
								  :clearable="!required"
								   :disabled="disabled"
								   :name="`${validationPrefix}district_id`"
								   @update:modelValue="handleLocationInput"
								   :label="$t('label.district')"/>
			</column>

			<column cols="12" :lg="slim ? 6 : 12">
				<form-input v-model="data[`${propPrefix}postcode`]" :help="!disableHelp ? $t(helpPrefix + '.postcode', null) : null"
							:rules="{required: required || data[`${propPrefix}city`] || data[`${propPrefix}street`]}"
							:disabled="disabled"
							:name="`${validationPrefix}postcode`"
							:placeholder="withPlaceholders ? $t('placeholder.postcode') : null"
							@update:modelValue="handleLocationInput"
							:label="$t('label.postcode')"/>
			</column>

			<column cols="12" :lg="slim ? 6 : 12">
				<form-input v-model="data[`${propPrefix}city`]"
							:rules="{required: required || data[`${propPrefix}street`]}"
							:disabled="disabled"
							:name="`${validationPrefix}city`"
							:placeholder="withPlaceholders ? $t('placeholder.city') : null"
							@update:modelValue="handleLocationInput"
							:label="$t('label.city')" :help="!disableHelp ? $t(helpPrefix + '.city', null) : null"/>
			</column>

			<column v-if="street" cols="12">
				<form-input v-model="data[`${propPrefix}street`]"
							:rules="{required: streetRequired, max: 60, min: 3}"
							:disabled="disabled"
							:name="`${validationPrefix}street`"
							:placeholder="withPlaceholders ? $t('placeholder.street') : null"
							@update:modelValue="handleLocationInput"
							:label="$t('label.street')" :help="!disableHelp ? $t(helpPrefix + '.street', null) : null">
				</form-input>
			</column>

			<column :cols="12" v-if="locationName">
				<form-input v-model="data[`${propPrefix}location_name`]"
							:rules="{max: 60, min: 3}"
							:disabled="disabled"
							:name="`${validationPrefix}location_name`"
							:label="$t('label.location_name')"
							:help="!disableHelp ? $t(helpPrefix + '.location_name', null) : null">
				</form-input>
			</column>
		</template>

		<column :cols="12" v-if="email">
			<form-input v-model="data[`${propPrefix}email`]" type="email"
						:rules="{required: required, min: 3, max: 150}"
						:disabled="disabled"
						:name="`${validationPrefix}email`"
						:label="$t('label.email')"
						:help="!disableHelp ? $t(helpPrefix + '.email', null) : null">
			</form-input>
		</column>

		<column v-if="phone" cols="12" :lg="slim && emergencyPhone ? 6 : 12">
			<form-input v-model="data[`${propPrefix}phone`]" type="tel"
						:rules="{required: phoneRequired, min: 5, max: 30, phone: true}"
						:disabled="disabled"
						placeholder="+49"
						:name="`${validationPrefix}phone`"
						:label="$t('label.phone')"
						:help="!disableHelp ? $t(helpPrefix + '.phone', null) : null">
			</form-input>
		</column>

		<column v-if="emergencyPhone" cols="12" :lg="slim && phone ? 6 : 12">
			<form-input v-model="data[`${propPrefix}emergency_phone`]" type="tel"
						:rules="{min: 5, max: 30, phone: true}"
						:disabled="disabled"
						placeholder="+49"
						:name="`${validationPrefix}emergency_phone`"
						:label="$t('label.emergency_phone')"
						:help="!disableHelp ? $t(helpPrefix + '.emergency_phone', null) : null">
			</form-input>
		</column>
	</row>

</template>

<script>
import FormInputsMixin from "../FormInputsMixin";

export default {
	name: 'ContactFormInputs',


	mixins: [FormInputsMixin],

	props: {

		// Whether to display the name input
		name: {
			type: Boolean,
			default: false
		},

		// Whether to display the location name input
		locationName: {
			type: Boolean,
			default: false
		},

		// Whether to display the district input
		withDistrict: {
			type: Boolean,
			default: false
		},

		// Whether to hide the address fields
		noAddress: {
			type: Boolean,
			default: false
		},

		// Whether to display the email input
		email: {
			type: Boolean,
			default: false
		},

		// Whether to display the phone input
		phone: {
			type: Boolean,
			default: false
		},

		// Whether to display the street input
		street: {
			type: Boolean,
			default: true
		},

		// Whether to display the emergency phone input
		emergencyPhone: {
			type: Boolean,
			default: false
		},

		// Whether the basic info is required
		required: {
			type: Boolean,
			default: false
		},

		// Whether to show any placeholders
		withPlaceholders: {
			type: Boolean,
			default: false
		},

		// Whether to not show any help text
		disableHelp: {
			type: Boolean,
			default: false
		},

		// Whether the country is required
		countryRequired: {
			type: Boolean,
			default: false
		},

		// Whether the street is required
		streetRequired: {
			type: Boolean,
			default: false
		},

		// Whether the phone is required
		phoneRequired: {
			type: Boolean,
			default: false
		},

		helpPrefix: {
			type: String,
			default: 'help.contact'
		},

		// The prefix to prepend on the input names, so validation errors on the backend can be assigned to the correct input field
		validationPrefix: {
			type: String,
			default: ''
		},

		// The prefix to prepend on the input data prop to access the value
		propPrefix: {
			type: String,
			default: ''
		},

		// Whether to show the inputs next to each other on large screens
		slim: {
			type: Boolean,
			default: false
		},

		// The color to style the input components
		color: {
			type: String,
			default: null
		},
	},

	data() {
		return {

			// Whether to show the states input. We hide it, if we have no states to select according to the selected country
			showStates: true,

			// Whether to show the district input. We hide it, if we have no districts to select according to the selected country state
			showDistricts: this.withDistrict,

			hasLoadedCountries: false,
			hasLoadedCountryStates: false,
		}
	},

	watch: {
		showStates(showStates) {
			if (!showStates) {
				this.data['state_code'] = null;
				this.showDistricts = false;
			}
		},

		showDistricts(showDistricts) {
			if (!showDistricts) {
				this.data['district_id'] = null;
			}
		}
	},

	methods: {
		handleLocationInput() {
			this.$emit('locationchanged', this.data);
		},

		handleChangedCountry(countryOption) {

			// The given countryOption is null when the countries are not yet loaded, but a country is selected whereby we do not know if it has states.
			// As soon as the countries are loaded, the handler is called a second time whereby we can determine if the selected country has states or not
			if (!this.hasLoadedCountries) {
				this.hasLoadedCountries = true;
				return;
			}

			this.showStates = countryOption?.states_count > 0;
		},

		handleChangedCountryState(countryStateOption) {

			// The given countryOption is null when the countries are not yet loaded, but a country is selected whereby we do not know if it has states.
			// As soon as the countries are loaded, the handler is called a second time whereby we can determine if the selected country has states or not
			if (!this.hasLoadedCountryStates) {
				this.hasLoadedCountryStates = true;
				return;
			}

			this.showDistricts = this.withDistrict && countryStateOption?.districts_count > 0;
		}
	}

}
</script>
